import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import {
	monthGrid,
	monthOf,
	shiftMonth,
	weekStartOf,
} from '@/features/scheduling/availability'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import { format, parseISO } from 'date-fns'
import { useMemo, useState } from 'react'
import { ScrollView, View } from 'react-native'

/**
 * Monday first, as the week is counted here and as the booking strip shows it.
 *
 * Three letters rather than one. A single letter cannot tell Tuesday from Thursday or Saturday
 * from Sunday, so four of the seven columns were only identifiable by counting along from
 * Monday.
 */
const WEEKDAYS = [
	{ name: 'Monday', short: 'Mon' },
	{ name: 'Tuesday', short: 'Tue' },
	{ name: 'Wednesday', short: 'Wed' },
	{ name: 'Thursday', short: 'Thu' },
	{ name: 'Friday', short: 'Fri' },
	{ name: 'Saturday', short: 'Sat' },
	{ name: 'Sunday', short: 'Sun' },
]

const yearsIn = (earliest: string, latest: string) => {
	const from = Number.parseInt(earliest.slice(0, 4), 10)
	const to = Number.parseInt(latest.slice(0, 4), 10)
	// Newest first: the dates asked for here are looked back to, never forward.
	return Array.from({ length: to - from + 1 }, (_, i) => to - i)
}

/** The month and its year, and — where there is more than one worth reaching — the way into them. */
const Head = ({
	month,
	onShift,
	onYears,
	showingYears,
}: {
	month: string
	onShift: (count: number) => void
	/** Absent where the years are not worth offering, which takes the label out of the tab order. */
	onYears?: () => void
	showingYears: boolean
}) => (
	<HStack className="items-center justify-between">
		<Pressable
			onPress={() => onShift(-1)}
			disabled={showingYears}
			accessibilityRole="button"
			accessibilityLabel="Previous month"
			hitSlop={12}
			// In `style` rather than a class: Pressable's press feedback is an animated style
			// carrying an opacity, and an inline style beats a class, so the month arrows stayed
			// at full strength behind the year list they were supposed to have made way for.
			style={showingYears ? { opacity: 0 } : undefined}
		>
			<Glyph name="chevron" size={18} color={colors.ink} rotate={180} />
		</Pressable>
		{/* The label is the control: the way to 1985 is through the year, not through the arrows.
		    It says so by being underlined, which is how everything else in this app says a word can
		    be pressed. A caret beside it was a second, smaller thing to aim at saying the same.
		    
		    Where the years are not on offer it is a label and drawn as one — no underline, no
		    press, nothing in the tab order. An underlined word that does nothing is worse than a
		    plain one, and a booking window a few weeks wide has no year to go to. */}
		{onYears ? (
			<Pressable
				onPress={onYears}
				accessibilityRole="button"
				accessibilityState={{ expanded: showingYears }}
				accessibilityLabel={`${format(
					parseISO(`${month}T00:00:00`),
					'MMMM yyyy'
				)}. Choose a year`}
				hitSlop={10}
			>
				{/* Sentence case. Set in caps it read as a banner across a control whose job is to
				    be scanned, and a month is a name rather than an announcement. */}
				<Text className="font-display text-h6 underline underline-offset-4">
					{format(parseISO(`${month}T00:00:00`), 'MMMM yyyy')}
				</Text>
			</Pressable>
		) : (
			<Text className="font-display text-h6">
				{format(parseISO(`${month}T00:00:00`), 'MMMM yyyy')}
			</Text>
		)}
		<Pressable
			onPress={() => onShift(1)}
			disabled={showingYears}
			accessibilityRole="button"
			accessibilityLabel="Next month"
			hitSlop={12}
			style={showingYears ? { opacity: 0 } : undefined}
		>
			<Glyph name="chevron" size={18} color={colors.ink} />
		</Pressable>
	</HStack>
)

/**
 * The brand's own calendar, for picking a day out of any stretch of years.
 *
 * The month grid is the one the booking flow draws, off the same `monthGrid`, so a calendar is
 * one thing in this app rather than two that drift. What is added here is the year: a birth date
 * is forty years back, and a picker that only steps a month at a time is four hundred and eighty
 * taps from 1985. Tapping the month's name turns the grid into its years.
 *
 * What the value marks is the one thing that varies: a disc under a day, or a fill across the
 * week it falls in. The booking flow needs the second, which is why there is no longer a second
 * calendar over there.
 */
export const DatePicker = ({
	value,
	onChange,
	earliest,
	latest,
	weekdays = true,
	highlight = 'day',
	years: yearsEnabled = true,
	surface = 'bg-white',
}: {
	/** The chosen day, `yyyy-MM-dd`, or null when nothing is chosen yet. */
	value: string | null
	onChange: (day: string) => void
	earliest: string
	latest: string
	/**
	 * The row of weekday initials. On for a date being found by its day of the week, which is how
	 * an appointment is looked for; off where the weekday is nothing to do with the answer, as it
	 * is for a birth date, where a column of S M T W T F S is just noise over the numbers.
	 */
	weekdays?: boolean
	/**
	 * Whether the month's name opens its years.
	 *
	 * On for a date that could be in any of them — a birth date is forty years back, and a picker
	 * that steps a month at a time is four hundred and eighty taps from 1985. Off where the range
	 * is a few weeks wide, as a booking window is: there is no year to go to, and a control that
	 * leads nowhere is worth less than the label it replaced.
	 */
	years?: boolean
	/**
	 * What the grid sits on, as a class. The calendar draws its own card because most callers
	 * put it straight onto the pink, where white is what a card is — but a caller that has
	 * already decided the surface, as a sheet has, passes its own and gets one card instead of
	 * a ring of one colour drawn around another.
	 */
	surface?: string
	/**
	 * What the value marks. `day` puts a disc under the one date, which is what a birth date or
	 * a last cleaning is. `week` fills the whole row it falls in, for a screen whose unit is a
	 * week — the booking flow looks at seven days at a time, so a single marked day there would
	 * name something the screen cannot show.
	 */
	highlight?: 'day' | 'week'
}) => {
	const [month, setMonth] = useState(() => monthOf(value ?? latest))
	const [showingYears, setShowingYears] = useState(false)
	const rows = monthGrid(month, highlight === 'week' && value ? weekStartOf(value) : '', {
		earliestBookableDate: earliest,
		maxBookableDate: latest,
	})
	const years = useMemo(() => yearsIn(earliest, latest), [earliest, latest])

	return (
		<VStack gap={14} className={cn('rounded-note p-4', surface)}>
			<Head
				month={month}
				onShift={(count) => setMonth(shiftMonth(month, count))}
				onYears={yearsEnabled ? () => setShowingYears((was) => !was) : undefined}
				showingYears={showingYears}
			/>

			{showingYears ? (
				<ScrollView className="max-h-[236px]" showsVerticalScrollIndicator={false}>
					<View className="flex-row flex-wrap">
						{years.map((year) => {
							const isCurrent = month.startsWith(String(year))
							return (
								<View key={year} className="w-1/4 p-1">
									<Pressable
										onPress={() => {
											setMonth(`${year}${month.slice(4)}`)
											setShowingYears(false)
										}}
										accessibilityRole="button"
										accessibilityState={{ selected: isCurrent }}
										accessibilityLabel={String(year)}
										className={cn(
											'items-center justify-center rounded-brand py-2.5',
											isCurrent ? 'bg-ink' : 'bg-blush'
										)}
									>
										<Text weight="bold" tone={isCurrent ? 'white' : 'ink'}>
											{year}
										</Text>
									</Pressable>
								</View>
							)
						})}
					</View>
				</ScrollView>
			) : (
				<VStack gap={2}>
					{weekdays && (
						<View className="flex-row">
							{WEEKDAYS.map((day) => (
								<Text
									key={day.name}
									accessibilityLabel={day.name}
									size="body3"
									tone="muted"
									className="flex-1 text-center"
								>
									{day.short}
								</Text>
							))}
						</View>
					)}
					{rows.map((row) => {
						/*
						 * A chosen week is the day in ink and its six neighbours in the ground pink.
						 *
						 * It was one ink pill across all seven, which said the week was picked but
						 * lost which day was tapped inside it — and a member who taps the fifteenth
						 * wants to see the fifteenth acknowledged, not the week it happens to fall
						 * in. A fill behind the rest still carries the week, quietly, so both facts
						 * are on the screen at once.
						 *
						 * The fill is the page's own pink rather than a grey. Grey was chosen while
						 * this calendar still sat on white; on the blush card it now sits on it was
						 * a cool neutral over a warm one, and two greys from different families
						 * make mud. Blush card, ground week, ink day is three steps of one family
						 * and introduces no colour the screen did not already have.
						 */
						const isWeek = highlight === 'week' && row.some((c) => c.isInSelectedWeek)
						return (
							<View
								key={row[0]?.date}
								className={cn(
									'flex-row',
									isWeek && highlight !== 'week' && 'rounded-full bg-ink'
								)}
							>
								{row.map((cell) => {
									const isChosen =
										highlight === 'week' ? cell.date === value : cell.date === value
									const inWeek = highlight === 'week' && cell.isInSelectedWeek
									return (
										<Pressable
											key={cell.date}
											disabled={!cell.isSelectable}
											onPress={() => onChange(cell.date)}
											accessibilityRole="button"
											accessibilityState={{
												selected: isChosen || inWeek,
												disabled: !cell.isSelectable,
											}}
											accessibilityLabel={format(
												parseISO(`${cell.date}T00:00:00`),
												'EEEE d MMMM yyyy'
											)}
											className="flex-1 items-center py-1"
										>
											<View
												className={cn(
													'h-9 w-9 items-center justify-center rounded-full',
													isChosen && 'bg-ink'
												)}
												style={
													inWeek && !isChosen
														? { backgroundColor: colors.ground }
														: undefined
												}
											>
												<Text
													weight={isChosen || inWeek ? 'bold' : 'book'}
													tone={isChosen ? 'white' : cell.isSelectable ? 'ink' : 'muted'}
													className={cn(!cell.inMonth && !isChosen && 'opacity-40')}
												>
													{cell.dayOfMonth}
												</Text>
											</View>
										</Pressable>
									)
								})}
							</View>
						)
					})}
				</VStack>
			)}
		</VStack>
	)
}
